import type { AppConfig } from '@/types/config';
import environment from './environment';

// Global application configuration
export const appConfig: AppConfig = {
  api: {
    baseUrl: environment.apiBaseUrl,
    apiUrl: environment.apiBaseUrl,
    timeout: environment.requestTimeout,
    retryAttempts: 3,
    retryDelay: 1000,
    staticAuth: environment.staticAuth,
    imageBaseUrl: environment.imageBaseUrl,
  },
  auth: {
    providers: ['keycloak'],
    tokenRefreshThreshold: 5 * 60 * 1000, // 5 minutes
    sessionTimeout: 30 * 60 * 1000, // 30 minutes
    rememberMeDuration: 7 * 24 * 60 * 60 * 1000, // 7 days
    // Keycloak OAuth2 Configuration
    keycloak: environment.mockKeycloak
      ? {
          // Mock Keycloak configuration for development
          issuer: 'http://localhost:8000',
          realm: 'adminportal',
          clientId: 'mock-client',
          redirectUri: 'http://localhost:8000/auth/callback',
          silentCheckUri: 'http://localhost:8000/auth/silent-check',
          scopes: ['openid', 'profile', 'email'],
          tokenRefreshThreshold: 300000, // 5 minutes
        }
      : {
          // Production Keycloak configuration
          issuer: process.env.REACT_APP_AUTH_ISSUER || '',
          realm: process.env.REACT_APP_AUTH_REALM || '',
          clientId: process.env.REACT_APP_AUTH_CLIENT_ID || '',
          redirectUri:
            process.env.REACT_APP_AUTH_REDIRECT_URI ||
            'http://localhost:8000/auth/callback',
          silentCheckUri: process.env.REACT_APP_AUTH_SILENT_CHECK_URI || '',
          scopes: (
            process.env.REACT_APP_AUTH_SCOPES || 'openid profile email'
          ).split(' '),
          tokenRefreshThreshold: parseInt(
            process.env.REACT_APP_AUTH_TOKEN_REFRESH_THRESHOLD || '300000',
            10,
          ), // 5 minutes default
        },
  },
  cache: {
    defaultTTL: environment.cacheTTL,
    maxSize: environment.cacheMaxSize,
    storageType: 'memory',
  },
  features: {
    auditLogging: environment.auditLogging,
    telemetry: environment.telemetryEnabled,
    multiCountry: true,
    darkMode: true,
    realTimeUpdates: true,
  },
  menu: {
    source: (process.env.REACT_APP_MENU_SOURCE || 'dynamic') as
      | 'static'
      | 'dynamic',
  },
  ui: {
    theme: 'light',
    language: 'en-US',
    timezone: 'UTC',
    dateFormat: 'YYYY-MM-DD',
    numberFormat: 'en-US',
  },
  countries: {
    supported: ['US', 'CA', 'GB', 'DE', 'FR', 'AU', 'JP', 'SG'],
    default: 'US',
  },
  reports: {
    maxExportRows: 10000,
    defaultPageSize: 20,
    maxPageSize: 100,
    cacheTimeout: 5 * 60 * 1000, // 5 minutes
  },
};

export const config: AppConfig = appConfig;

// Validation function for required Keycloak configuration
function validateKeycloakConfig(keycloakConfig: any): void {
  const requiredFields = ['issuer', 'realm', 'clientId', 'redirectUri'];
  const missingFields: string[] = [];

  requiredFields.forEach((field) => {
    if (!keycloakConfig[field] || keycloakConfig[field].trim() === '') {
      missingFields.push(field);
    }
  });

  if (missingFields.length > 0) {
    const errorMessage = `Missing required Keycloak configuration: ${missingFields.join(', ')}. Please set the following environment variables: ${missingFields.map((field) => `REACT_APP_AUTH_${field.toUpperCase()}`).join(', ')}`;

    if (process.env.NODE_ENV === 'development') {
      console.warn('Keycloak configuration warning:', errorMessage);
    } else {
      throw new Error(errorMessage);
    }
  }

  // Validate URL formats
  if (keycloakConfig.issuer && !keycloakConfig.issuer.startsWith('http')) {
    throw new Error(
      'REACT_APP_AUTH_ISSUER must be a valid URL starting with http:// or https://',
    );
  }

  if (
    keycloakConfig.redirectUri &&
    !keycloakConfig.redirectUri.startsWith('http')
  ) {
    throw new Error(
      'REACT_APP_AUTH_REDIRECT_URI must be a valid URL starting with http:// or https://',
    );
  }
}

// Validate Keycloak configuration
try {
  validateKeycloakConfig(config.auth.keycloak);
} catch (error) {
  console.error('Keycloak configuration error:', error);
  if (process.env.NODE_ENV !== 'development') {
    throw error;
  }
}

export default config;
